import React, { useEffect, useRef, useState } from "react";
import { api, resolveImage } from "../../lib/api";
import { Plus, Edit2, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["T-shirt", "Maglie", "Felpe", "Camicie", "Pantaloni", "Jeans", "Shorts", "Gonne", "Vestiti", "Giacche", "Cappotti", "Completi", "Tute", "Sneakers", "Scarpe", "Sandali", "Stivali", "Ciabatte", "Cappelli", "Borse", "Cinture", "Calze", "Accessori"];
const GENDERS = [
    { value: "neonato", label: "Neonato" },
    { value: "bambino", label: "Bambino" },
    { value: "bambina", label: "Bambina" },
    { value: "ragazzo", label: "Ragazzo" },
    { value: "ragazza", label: "Ragazza" },
    { value: "unisex", label: "Unisex" },
];
const AGE_GROUPS = ["0-3 mesi", "3-6 mesi", "6-12 mesi", "12-18 mesi", "18-24 mesi", "2-4 anni", "4-6 anni", "6-8 anni", "8-10 anni", "10-12 anni", "12-14 anni", "14-16 anni"];
const SIZES_CLOTHING = ["XS", "S", "M", "L", "XL", "50", "56", "62", "68", "74", "80", "86", "92", "98", "104", "110", "116", "122", "128", "134", "140", "146", "152", "158", "164"];
const SIZES_SHOES = ["16", "17", "18", "19", "20", "21", "22", "23", "24", "25", "26", "27", "28", "29", "30", "31", "32", "33", "34", "35", "36", "37", "38", "39", "40"];
const SEASONS = ["Primavera/Estate", "Autunno/Inverno", "Tutto l'anno"];

const blank = {
    title: "",
    brand: "",
    category: CATEGORIES[0],
    description: "",
    price: 0,
    sale_price: null,
    on_sale: false,
    size: "M",
    sizes: [],
    condition: "Nuovo",
    measurements: {},
    shipping_info: "Spedizione rapida in tutta Italia. Consegna in 24/48h.",
    stock: 1,
    images: [],
    is_rare: false,
    is_featured: false,
    is_new: true,
    is_published: true,
    tags: [],
    age_group: "",
    gender: "unisex",
    color: "",
    material: "",
    season: "Tutto l'anno",
    fit: "",
};

export default function AdminProducts() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(false);
    const fileRef = useRef(null);

    const load = () => api.get("/admin/products").then(r => setItems(r.data));
    useEffect(() => { load(); }, []);

    const startNew = () => setEditing({ ...blank });
    const close = () => setEditing(null);

    const isShoes = editing && ["Sneakers", "Scarpe", "Sandali", "Stivali", "Ciabatte"].includes(editing.category);
    const sizeOptions = isShoes ? SIZES_SHOES : SIZES_CLOTHING;

    const save = async () => {
        setLoading(true);
        try {
            const payload = {
                ...editing,
                price: parseFloat(editing.price),
                sale_price: editing.sale_price ? parseFloat(editing.sale_price) : null,
                stock: parseInt(editing.stock || 0),
                age_group: editing.age_group || null,
                gender: editing.gender || null,
                color: editing.color || null,
                material: editing.material || null,
                season: editing.season || null,
                fit: editing.fit || null,
            };
            if (editing.id) {
                await api.put(`/admin/products/${editing.id}`, payload);
                toast.success("Prodotto aggiornato");
            } else {
                await api.post("/admin/products", payload);
                toast.success("Prodotto creato");
            }
            await load();
            close();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Salvataggio fallito");
        } finally {
            setLoading(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare questo prodotto?")) return;
        await api.delete(`/admin/products/${id}`);
        toast.success("Eliminato");
        load();
    };

    const upload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        for (const file of files) {
            const fd = new FormData();
            fd.append("file", file);
            try {
                const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                setEditing(prev => ({ ...prev, images: [...prev.images, { url: r.data.url, alt: "" }] }));
                toast.success(`Caricato ${file.name}`);
            } catch (err) {
                toast.error(`Upload fallito: ${file.name}`);
            }
        }
        if (fileRef.current) fileRef.current.value = "";
    };

    const removeImage = (idx) => {
        setEditing(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));
    };

    const toggleSize = (s) => {
        setEditing(prev => {
            const has = prev.sizes.includes(s);
            return { ...prev, sizes: has ? prev.sizes.filter(x => x !== s) : [...prev.sizes, s] };
        });
    };

    return (
        <div data-testid="admin-products">
            <div className="flex justify-between items-center mb-10">
                <div>
                    <div className="overline mb-2">Catalogo</div>
                    <h1 className="font-display text-4xl text-[color:var(--ms-charcoal)]">Prodotti</h1>
                </div>
                <button onClick={startNew} className="bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] px-5 py-3 text-xs tracking-[0.2em] uppercase flex items-center gap-2 hover:bg-[color:var(--ms-sage-dark)] transition-colors" data-testid="admin-new-product">
                    <Plus size={14} /> Nuovo prodotto
                </button>
            </div>

            {items.length === 0 ? (
                <div className="panel p-16 text-center text-[color:var(--ms-muted)]" data-testid="admin-products-empty">
                    Nessun prodotto ancora. Aggiungi il tuo primo capo.
                </div>
            ) : (
                <div className="panel overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-left text-[color:var(--ms-muted)] text-xs uppercase tracking-[0.2em]">
                            <tr>
                                <th className="px-4 py-3 border-b border-[color:var(--ms-border)]">Immagine</th>
                                <th className="px-4 py-3 border-b border-[color:var(--ms-border)]">Titolo</th>
                                <th className="px-4 py-3 border-b border-[color:var(--ms-border)]">Categoria</th>
                                <th className="px-4 py-3 border-b border-[color:var(--ms-border)]">Età</th>
                                <th className="px-4 py-3 border-b border-[color:var(--ms-border)]">Prezzo</th>
                                <th className="px-4 py-3 border-b border-[color:var(--ms-border)]">Stock</th>
                                <th className="px-4 py-3 border-b border-[color:var(--ms-border)]"></th>
                            </tr>
                        </thead>
                        <tbody className="text-[color:var(--ms-charcoal)]">
                            {items.map(p => (
                                <tr key={p.id} className="border-b border-[color:var(--ms-border)]" data-testid={`product-row-${p.id}`}>
                                    <td className="px-4 py-3">
                                        {p.images?.[0]?.url ? (
                                            <img src={resolveImage(p.images[0].url)} alt="" className="w-12 h-16 object-cover bg-[color:var(--ms-cream)]" />
                                        ) : <div className="w-12 h-16 bg-[color:var(--ms-cream)]" />}
                                    </td>
                                    <td className="px-4 py-3">{p.title}<div className="text-xs text-[color:var(--ms-muted)]">{p.brand}</div></td>
                                    <td className="px-4 py-3">{p.category}</td>
                                    <td className="px-4 py-3 text-xs text-[color:var(--ms-muted)]">{p.age_group || "—"}</td>
                                    <td className="px-4 py-3">€{p.price.toFixed(2)}{p.on_sale && p.sale_price ? <span className="text-[color:var(--ms-terracotta)] ml-2">€{p.sale_price.toFixed(2)}</span> : null}</td>
                                    <td className="px-4 py-3">{p.stock}</td>
                                    <td className="px-4 py-3 flex gap-2">
                                        <button onClick={() => setEditing({ ...blank, ...p })} className="p-2 hover:text-[color:var(--ms-sage-dark)]" data-testid={`product-edit-${p.id}`}><Edit2 size={14} /></button>
                                        <button onClick={() => remove(p.id)} className="p-2 hover:text-[color:var(--ms-terracotta)]" data-testid={`product-delete-${p.id}`}><Trash2 size={14} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="product-editor">
                    <div className="bg-[color:var(--ms-warm-white)] border border-[color:var(--ms-border)] w-full max-w-3xl my-8 p-8">
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <div className="overline">{editing.id ? "Modifica" : "Nuovo"}</div>
                                <h2 className="font-display text-3xl text-[color:var(--ms-charcoal)]">{editing.id ? "Modifica Prodotto" : "Nuovo Prodotto"}</h2>
                            </div>
                            <button onClick={close} className="text-[color:var(--ms-muted)] hover:text-[color:var(--ms-charcoal)]"><X size={20} /></button>
                        </div>

                        <div className="grid md:grid-cols-2 gap-5">
                            <Field label="Nome prodotto"><input type="text" value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} className={inputCls} data-testid="form-title" /></Field>
                            <Field label="Brand"><input type="text" value={editing.brand} onChange={e => setEditing({ ...editing, brand: e.target.value })} className={inputCls} placeholder="Il Gufo, Monnalisa, …" data-testid="form-brand" /></Field>
                            <Field label="Categoria">
                                <select value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value, sizes: [] })} className={inputCls} data-testid="form-category">
                                    {CATEGORIES.map(b => <option key={b}>{b}</option>)}
                                </select>
                            </Field>
                            <Field label="Chi indossa">
                                <select value={editing.gender} onChange={e => setEditing({ ...editing, gender: e.target.value })} className={inputCls} data-testid="form-gender">
                                    <option value="">— Seleziona —</option>
                                    {GENDERS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                                </select>
                            </Field>
                            <Field label="Fascia d'età">
                                <select value={editing.age_group || ""} onChange={e => setEditing({ ...editing, age_group: e.target.value })} className={inputCls} data-testid="form-age">
                                    <option value="">— Seleziona —</option>
                                    {AGE_GROUPS.map(a => <option key={a}>{a}</option>)}
                                </select>
                            </Field>
                            <Field label="Colore"><input type="text" value={editing.color || ""} onChange={e => setEditing({ ...editing, color: e.target.value })} className={inputCls} placeholder="Beige, Rosa cipria, …" data-testid="form-color" /></Field>
                            <Field label="Materiale"><input type="text" value={editing.material || ""} onChange={e => setEditing({ ...editing, material: e.target.value })} className={inputCls} placeholder="100% cotone" data-testid="form-material" /></Field>
                            <Field label="Stagione">
                                <select value={editing.season || ""} onChange={e => setEditing({ ...editing, season: e.target.value })} className={inputCls} data-testid="form-season">
                                    <option value="">—</option>
                                    {SEASONS.map(s => <option key={s}>{s}</option>)}
                                </select>
                            </Field>
                            <Field label="Vestibilità"><input type="text" value={editing.fit || ""} onChange={e => setEditing({ ...editing, fit: e.target.value })} className={inputCls} placeholder="Regolare, Slim, …" data-testid="form-fit" /></Field>
                            <Field label="Stato"><input value={editing.condition} onChange={e => setEditing({ ...editing, condition: e.target.value })} className={inputCls} /></Field>
                            <Field label="Prezzo (€)"><input type="number" step="0.01" value={editing.price} onChange={e => setEditing({ ...editing, price: e.target.value })} className={inputCls} data-testid="form-price" /></Field>
                            <Field label="Prezzo scontato (€)"><input type="number" step="0.01" value={editing.sale_price || ""} onChange={e => setEditing({ ...editing, sale_price: e.target.value })} className={inputCls} data-testid="form-sale-price" /></Field>
                            <Field label="Quantità"><input type="number" value={editing.stock} onChange={e => setEditing({ ...editing, stock: e.target.value })} className={inputCls} data-testid="form-stock" /></Field>
                        </div>

                        <div className="mt-5">
                            <label className="overline block mb-2">Taglie disponibili {isShoes ? "(numero scarpe)" : "(vestibilità)"}</label>
                            <div className="flex flex-wrap gap-2">
                                {sizeOptions.map(s => (
                                    <button key={s} type="button" onClick={() => toggleSize(s)} className={`px-4 py-2 text-sm border ${editing.sizes?.includes(s) ? "border-[color:var(--ms-charcoal)] bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)]" : "border-[color:var(--ms-border)] text-[color:var(--ms-charcoal)]"}`}>{s}</button>
                                ))}
                            </div>
                        </div>

                        <Field label="Descrizione" className="mt-5">
                            <textarea rows={4} value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} className={inputCls} data-testid="form-description" />
                        </Field>

                        <Field label="Info spedizione" className="mt-5">
                            <textarea rows={2} value={editing.shipping_info} onChange={e => setEditing({ ...editing, shipping_info: e.target.value })} className={inputCls} />
                        </Field>

                        <div className="mt-6 flex gap-4 flex-wrap text-sm">
                            <Check label="In saldo" v={editing.on_sale} onChange={v => setEditing({ ...editing, on_sale: v })} testid="form-onsale" />
                            <Check label="In evidenza" v={editing.is_featured} onChange={v => setEditing({ ...editing, is_featured: v })} />
                            <Check label="Nuovo arrivo" v={editing.is_new} onChange={v => setEditing({ ...editing, is_new: v })} />
                            <Check label="Pubblicato" v={editing.is_published} onChange={v => setEditing({ ...editing, is_published: v })} />
                        </div>

                        <div className="mt-6">
                            <label className="overline mb-3 block">Immagini prodotto</label>
                            <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mb-3">
                                {editing.images.map((img, i) => (
                                    <div key={i} className="relative aspect-[3/4] bg-[color:var(--ms-cream)] overflow-hidden">
                                        <img src={resolveImage(img.url)} alt="" className="w-full h-full object-cover" />
                                        <button onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-white/90 p-1 text-[color:var(--ms-charcoal)] hover:text-[color:var(--ms-terracotta)]" data-testid={`img-remove-${i}`}><X size={12} /></button>
                                    </div>
                                ))}
                                <button onClick={() => fileRef.current?.click()} className="aspect-[3/4] border border-dashed border-[color:var(--ms-border)] hover:border-[color:var(--ms-charcoal)] flex flex-col items-center justify-center text-[color:var(--ms-muted)] text-xs gap-2 transition-colors" data-testid="image-upload-btn">
                                    <Upload size={18} /> CARICA
                                </button>
                            </div>
                            <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" data-testid="image-upload-input" />
                        </div>

                        <div className="mt-8 flex justify-end gap-3">
                            <button onClick={close} className="px-5 py-3 text-xs tracking-[0.2em] uppercase border border-[color:var(--ms-border)] text-[color:var(--ms-charcoal)] hover:border-[color:var(--ms-charcoal)]">Annulla</button>
                            <button onClick={save} disabled={loading} className="bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] px-6 py-3 text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--ms-sage-dark)] disabled:opacity-50" data-testid="form-save">{loading ? "SALVATAGGIO…" : "Salva Prodotto"}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const inputCls = "w-full bg-[color:var(--ms-warm-white)] border border-[color:var(--ms-border)] focus:border-[color:var(--ms-charcoal)] px-3 py-2.5 text-sm outline-none text-[color:var(--ms-charcoal)]";

const Field = ({ label, children, className = "" }) => (
    <div className={className}>
        <label className="overline block mb-2">{label}</label>
        {children}
    </div>
);

const Check = ({ label, v, onChange, testid }) => (
    <label className="flex items-center gap-2 text-[color:var(--ms-charcoal)] cursor-pointer">
        <input type="checkbox" checked={v} onChange={e => onChange(e.target.checked)} className="accent-[color:var(--ms-sage-dark)]" data-testid={testid} /> {label}
    </label>
);
